import type { MovementConfig } from '../config/movements';
import type { KitGroup, WizardLine } from '../types/wizard';
import { getDisplayLineEntries } from './scanStats';

export type LineLocationError = { lineId: string; message: string };

/** Legacy kit table: From Bay (SLOC_A) required before SAP post for LEAP outbound / relocation. */
export function movementRequiresFromBay(movement: MovementConfig): boolean {
  if (movement.wizardKind === 'relocation') return true;
  if (movement.id === 'allot') return true;
  if (movement.wizardKind === 'standard' && movement.flowFrom === 'leap' && movement.fromBin === 'CONDITION') {
    return true;
  }
  return false;
}

/** Legacy LeapIn (dehire received): To Bay picker per line. */
export function movementRequiresToBay(movement: MovementConfig): boolean {
  return movement.id === 'dehire_received';
}

/** Legacy LeapIn: Short Qty input per line. */
export function movementShowsShortQty(movement: MovementConfig): boolean {
  return movement.id === 'dehire_received';
}

export function validateLineFromBay(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): LineLocationError[] {
  const errors: LineLocationError[] = [];
  for (const { line } of getDisplayLineEntries(lines, kitGroups)) {
    if (!line.fromBay.trim()) {
      const label = line.materialDesc.trim() || line.materialCode || 'Line';
      errors.push({
        lineId: line.id,
        message: `From bay required — ${label}`,
      });
    }
  }
  return errors;
}

export function validateLineToBay(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): LineLocationError[] {
  const errors: LineLocationError[] = [];
  for (const { line } of getDisplayLineEntries(lines, kitGroups)) {
    if (!line.toBay.trim()) {
      const label = line.materialDesc.trim() || line.materialCode || 'Line';
      errors.push({
        lineId: line.id,
        message: `To bay required — ${label}`,
      });
    }
  }
  return errors;
}


/** Legacy relocation: From Bin required for SAP FROM_BIN mapping. */
export function movementRequiresFromBin(movement: MovementConfig): boolean {
  return movement.wizardKind === 'relocation';
}

export function validateLineFromBin(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  movement?: MovementConfig
): LineLocationError[] {
  const errors: LineLocationError[] = [];
  const fallback = String(movement?.fromBin ?? '').trim();
  for (const { line } of getDisplayLineEntries(lines, kitGroups)) {
    if (!String(line.fromBin ?? '').trim() && !fallback) {
      const label = line.materialDesc.trim() || line.materialCode || 'Line';
      errors.push({
        lineId: line.id,
        message: `From bin required — ${label}`,
      });
    }
  }
  return errors;
}

export function validateMovementLocations(
  movement: MovementConfig,
  lines: WizardLine[],
  kitGroups: KitGroup[]
): LineLocationError[] {
  const errors: LineLocationError[] = [];
  if (movementRequiresFromBay(movement)) {
    errors.push(...validateLineFromBay(lines, kitGroups));
  }
  if (movementRequiresFromBin(movement)) {
    errors.push(...validateLineFromBin(lines, kitGroups, movement));
  }
  if (movementRequiresToBay(movement)) {
    errors.push(...validateLineToBay(lines, kitGroups));
  }
  return errors;
}
